import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { retrieveAccessTokenFromBackend } from './securityService';
import { beginRequest, endRequest } from './core/network/inflight';
import { apiOrigin } from './apiUrls';

let accessToken: string | null = null;

// Avoid spamming the backend with token requests if retrieval fails.
let tokenFetchFailed = false;

// Track ongoing token refresh to prevent multiple concurrent refresh attempts
let tokenRefreshPromise: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  // If a refresh is already in progress, wait for it
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  // Clear the expired token
  accessToken = null;
  tokenFetchFailed = false;

  // Start the refresh
  tokenRefreshPromise = (async () => {
    try {
      const freshToken = await retrieveAccessTokenFromBackend();
      if (freshToken) {
        accessToken = freshToken;
        return freshToken;
      } else {
        tokenFetchFailed = true;
        return null;
      }
    } catch {
      tokenFetchFailed = true;
      return null;
    } finally {
      // Clear the promise so future requests can trigger a new refresh if needed
      tokenRefreshPromise = null;
    }
  })();

  return tokenRefreshPromise;
}

async function getAuthHeader(): Promise<Record<string, string>> {
  let token = accessToken;

  // If we already know token retrieval is failing, don't keep trying.
  if (token == null && !tokenFetchFailed) {
    try {
      const freshToken = await retrieveAccessTokenFromBackend();

      if (freshToken) {
        accessToken = freshToken;
        token = freshToken;
      } else {
        tokenFetchFailed = true;
      }
    } catch {
      tokenFetchFailed = true;
      token = null;
    }
  }

  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const apiClient = axios.create({
  // `apiOrigin()` returns '' when VITE_API_URL is empty/unset, giving
  // a same-origin baseURL (relative paths) — lets the bundle run
  // unchanged behind a reverse proxy. Cross-origin mode (dev+prod
  // today) just passes the configured host through.
  baseURL: apiOrigin(),
  withCredentials: true,
});

apiClient.interceptors.request.use(async (config) => {
  const authHeader = await getAuthHeader();
  const headers = config.headers ?? {};
  Object.assign(headers as Record<string, string>, authHeader);
  config.headers = headers;
  // Increment the global in-flight counter; response interceptor
  // decrements on both success and failure paths so the TopBar
  // NetworkIndicator reflects live activity (plan Phase H.3).
  beginRequest();
  return config;
});

/**
 * Retry policy for transient server errors.
 *
 * 5xx responses are retried once with a small back-off (the server
 * failed, so a second attempt often succeeds).
 *
 * 429 is deliberately NOT retried. A 429 means the server has already
 * told us to slow down — retrying compounds the rate-limit pressure
 * instead of relieving it. Surface the error so the caller (or the
 * global errorToast) can show "Too many requests, try again shortly".
 * Requests with `_retryCount` already set are skipped — the policy is
 * deliberately single-shot to avoid runaway loops.
 */
const MAX_AUTO_RETRIES = 1;
const BASE_BACKOFF_MS = 500;

function shouldAutoRetry(error: AxiosError): boolean {
  const status = error.response?.status;
  if (typeof status !== 'number') {
    // Pure network/offline failures aren't retried automatically —
    // the OfflineBanner + manual refetch covers that UX.
    return false;
  }
  // 429 is explicitly excluded — see docstring above.
  return status >= 500;
}

function computeBackoffMs(attempt: number): number {
  // Exponential back-off: 500ms → 1s → 2s. (5xx-only now — we no
  // longer consume the 429 Retry-After header here, since 429 is
  // surfaced to the caller rather than retried.)
  return BASE_BACKOFF_MS * Math.pow(2, attempt - 1);
}

// Response interceptor: in-flight counter + 401 refresh + 5xx retry.
apiClient.interceptors.response.use(
  (response) => {
    endRequest();
    return response;
  },
  async (error: AxiosError) => {
    endRequest();
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    // 1) 401: refresh the token and retry once.
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshToken();

        if (newToken) {
          accessToken = newToken;
          return apiClient.request(originalRequest);
        } else {
          return Promise.reject(error);
        }
      } catch (error) {
        return Promise.reject(error);
      }
    }

    // 2) 5xx: single-shot retry with back-off.
    if (shouldAutoRetry(error)) {
      const attempt = (originalRequest._retryCount ?? 0) + 1;
      if (attempt <= MAX_AUTO_RETRIES) {
        originalRequest._retryCount = attempt;
        const delayMs = computeBackoffMs(attempt);
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return apiClient.request(originalRequest);
      }
    }

    return Promise.reject(error);
  },
);

// ── In-flight GET deduplication ─────────────────────────────────────────
// Two Home widgets + AdminDashboardView can all fetch /api/admin/dashboard/
// on the same tick. Without dedup that's three full round-trips burning the
// rate-limit budget. This Map keyed on the request's method+url+params
// returns the already-in-flight promise when a duplicate is issued,
// so the second caller shares the first's response.
//
// GET only — POSTs can be intentionally duplicated (double-send guards
// belong at the UI layer, not here), and PATCH/DELETE are side-effects
// that a second caller typically wants to run independently.

type DedupeKey = string;
const inflightGets = new Map<DedupeKey, Promise<unknown>>();

function dedupeKey(url: string, params?: Record<string, unknown>): DedupeKey {
  if (!params) return `GET ${url}`;
  try {
    const search = new URLSearchParams();
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([k, v]) => search.append(k, String(v)));
    const qs = search.toString();
    return qs ? `GET ${url}?${qs}` : `GET ${url}`;
  } catch {
    return `GET ${url}`;
  }
}

import type { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Like `apiClient.get<T>(url, config)` but coalesces concurrent calls
 * for the same (url, params) pair into a single request. Drop-in
 * replacement for apiClient.get inside services that are called from
 * multiple mounting components (admin dashboard, home dashboard, etc).
 *
 * If the same call is issued while an earlier identical call is still
 * in flight, the returned Promise resolves with the same response —
 * no second network request is made.
 */
export function dedupedGet<T>(
  url: string,
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> {
  const key = dedupeKey(url, config.params);
  const existing = inflightGets.get(key) as Promise<AxiosResponse<T>> | undefined;
  if (existing) return existing;
  const p = apiClient
    .get<T>(url, config)
    .finally(() => {
      // Remove from the inflight map on completion so a subsequent
      // fresh fetch can fire. Note: we do NOT cache the response —
      // callers that want stale-while-revalidate should use useFetch.
      inflightGets.delete(key);
    });
  inflightGets.set(key, p as Promise<unknown>);
  return p;
}

export { getAuthHeader };
